import { useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    InvoiceView,
    InvoiceViewSchema,
} from "@common/types/views/invoiceViews";
import { InvoicesViewContext } from "contexts/InvoicesViewContext";
import { PaginatedInvoicesTableContextProvider } from "@components/invoices/PaginatedInvoicesTableContextProvider";
import { useStandaloneInvoicesView } from "@components/invoices/useStandaloneInvoicesView";
import { InvoicesViewContainer } from "@components/invoices/views/invoicesView/InvoicesViewContainer";
import { buildInvoicesTaskFilters } from "@components/tasks/TaskBody/Invoices/invoicesTaskQuery";
import {
    TemplateTaskDataTable,
    TemplateTaskTableProps,
} from "./TemplateTaskDataTable";
import { ResolvedTaskView } from "./useResolvedTaskView";

interface Props {
    templateView: InvoiceView;
    onTemplateViewChange: (view: InvoiceView) => void;
    resolvedView: ResolvedTaskView;
    workspaceId: string | null;
}

const ResolvedInvoicesTable = ({
    view,
    onViewChange,
    emptyView,
}: TemplateTaskTableProps<InvoiceView>) => {
    const contextValue = useStandaloneInvoicesView();
    const apiFilters = useMemo(
        () => buildInvoicesTaskFilters({ viewFilters: view.filters }),
        [view.filters],
    );

    if (!apiFilters) {
        return emptyView();
    }

    return (
        <InvoicesViewContext.Provider value={contextValue}>
            <PaginatedInvoicesTableContextProvider overlayFilters={apiFilters}>
                <InvoicesViewContainer
                    view={view}
                    onInvoiceClick={noop}
                    hasConnectedInvoiceAccounts={false}
                    onClearFilters={noop}
                    emptyView={emptyView}
                    onViewChange={onViewChange}
                />
            </PaginatedInvoicesTableContextProvider>
        </InvoicesViewContext.Provider>
    );
};

const renderInvoicesTable = (props: TemplateTaskTableProps<InvoiceView>) => (
    <ResolvedInvoicesTable {...props} />
);

/**
 * The invoices a template task would show for the previewed client. Read-only:
 * no selection, bulk actions or row details.
 */
export const TemplateInvoicesDataTable = (props: Props) => (
    <TemplateTaskDataTable
        {...props}
        viewSchema={InvoiceViewSchema}
        rowsLabel="invoices"
        renderTable={renderInvoicesTable}
    />
);
